import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { Toaster } from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { checkRequired, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const _API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const _apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const WS_URL = _apiUrl.replace(/^http/, 'ws').replace(/\/api\/?$/, '') + '/ws/notifications/';

const STATUS_LABELS = { nouveau: 'Nouveau', membre: 'Membre régulier', serviteur: 'Serviteur', invite: 'Invité' };
const STATUS_COLORS = {
  nouveau:   { bg: '#fef9c3', text: '#854d0e', dot: '#ca8a04' },
  membre:    { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  serviteur: { bg: '#ede9fe', text: '#5b21b6', dot: '#7c3aed' },
  invite:    { bg: '#f0fdf4', text: '#166534', dot: '#16a34a' },
};
const GENDER_LABELS = { homme: 'Homme', femme: 'Femme', enfant: 'Enfant' };

/* ── QR Scanner ── */
function QRScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let active = true;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current && active) {
          const video = videoRef.current;
          video.srcObject = stream;
          video.onloadedmetadata = () => {
            video.play().catch(() => {});
            if (active) setScanning(true);
          };
        }
      })
      .catch(() => setError("Impossible d'accéder à la caméra."));
    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (!scanning) return;
    let jsQR;
    import('jsqr').then((m) => { jsQR = m.default; tick(); });
    function tick() {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        rafRef.current = requestAnimationFrame(tick); return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      if (jsQR) {
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) { cancelAnimationFrame(rafRef.current); onScan(code.data); return; }
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 100, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: 'rgba(0,0,0,0.8)' }}>
        <span style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>Scanner QR</span>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontWeight: 700 }}>Fermer</button>
      </div>
      <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {error ? (
          <div style={{ color: 'white', textAlign: 'center', padding: '20px', fontSize: '0.9rem' }}>{error}</div>
        ) : (
          <>
            <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: '200px', height: '200px', border: '3px solid white', borderRadius: '16px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)' }} />
            </div>
            <p style={{ position: 'absolute', bottom: '32px', color: 'white', fontSize: '0.88rem', textAlign: 'center', margin: 0 }}>Pointez vers le QR code de la carte</p>
          </>
        )}
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

/* ── Fiche participant complète ── */
function ParticipantCard({ participant, activityId, onPresent, onClose }) {
  const sc = STATUS_COLORS[participant.status] || STATUS_COLORS.invite;
  const initials = `${participant.first_name?.[0] || participant.full_name?.[0] || '?'}${participant.last_name?.[0] || ''}`.toUpperCase();
  const qrValue = participant.qr_code || participant.member_code || '';
  const [showQR, setShowQR] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const alreadyPresent = participant.attendance_records?.some?.(r => String(r.activity) === String(activityId));

  useEffect(() => {
    if (!participant.id) return;
    api.get(`/participants/${participant.id}/history/`)
      .then(r => setHistory(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [participant.id]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 -8px 32px rgba(0,0,0,0.25)' }}>
        {/* Handle */}
        <div style={{ width: '40px', height: '4px', background: '#e2e8f0', borderRadius: '999px', margin: '14px auto 0' }} />

        {/* ── En-tête profil ── */}
        <div style={{ padding: '18px 20px 0', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          {/* Photo ou initiales */}
          <div style={{ flexShrink: 0 }}>
            {participant.photo_url
              ? <img src={participant.photo_url} alt=""
                  style={{ width: '72px', height: '86px', borderRadius: '12px', objectFit: 'cover', objectPosition: 'top', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }} />
              : <div style={{ width: '72px', height: '86px', borderRadius: '12px', background: `${sc.dot}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                  <span style={{ fontSize: '1.6rem', fontWeight: 900, color: sc.dot }}>{initials}</span>
                </div>
            }
          </div>

          {/* Infos principales */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginBottom: '6px' }}>
              {participant.first_name || ''} <span style={{ color: sc.dot }}>{participant.last_name || participant.full_name || ''}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: '999px', background: sc.bg, color: sc.text, fontWeight: 700 }}>
                {STATUS_LABELS[participant.status] || participant.status}
              </span>
              {participant.member_code && (
                <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontWeight: 700 }}>
                  {participant.member_code}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '0.82rem', color: '#64748b' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 700, color: sc.dot, fontSize: '1rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={sc.dot} strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                {participant.attendance_count ?? 0} présences
              </span>
            </div>
          </div>

          {/* Bouton QR */}
          <button onClick={() => setShowQR(v => !v)}
            style={{ flexShrink: 0, background: showQR ? sc.dot : '#f1f5f9', border: 'none', borderRadius: '10px', padding: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={showQR ? 'white' : '#64748b'} strokeWidth="2">
              <rect x="3" y="3" width="5" height="5" rx="1"/><rect x="16" y="3" width="5" height="5" rx="1"/><rect x="3" y="16" width="5" height="5" rx="1"/>
              <line x1="16" y1="16" x2="21" y2="21"/>
            </svg>
          </button>
        </div>

        {/* QR code affiché */}
        {showQR && qrValue && (
          <div style={{ margin: '14px 20px', background: '#f8fafc', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', border: `2px solid ${sc.dot}30` }}>
            <QRCodeSVG value={qrValue} size={140} fgColor={sc.dot} level="M" />
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>{qrValue}</div>
          </div>
        )}

        {/* ── Grille infos détaillées ── */}
        <div style={{ margin: '14px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {[
            participant.gender      && { label: 'Genre',      val: GENDER_LABELS[participant.gender] || participant.gender },
            participant.phone       && { label: 'Téléphone',  val: participant.phone },
            participant.whatsapp    && { label: 'WhatsApp',   val: participant.whatsapp },
            participant.email       && { label: 'Email',      val: participant.email },
            participant.city        && { label: 'Ville',      val: participant.city },
            participant.profession  && { label: 'Profession', val: participant.profession },
            participant.birth_year  && { label: 'Né(e) en',   val: participant.birth_year },
            participant.created_at  && { label: 'Inscrit le', val: new Date(participant.created_at).toLocaleDateString('fr-FR') },
          ].filter(Boolean).map((row, i) => (
            <div key={i} style={{ background: '#f8fafc', borderRadius: '10px', padding: '10px 12px' }}>
              <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>{row.label}</div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.val}</div>
            </div>
          ))}
        </div>

        {/* ── Historique présences ── */}
        <div style={{ margin: '14px 20px 0' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: '8px' }}>
            Historique des présences
          </div>
          {loadingHistory ? (
            <div style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '8px 0' }}>Chargement...</div>
          ) : history.length === 0 ? (
            <div style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '8px 0' }}>Aucune présence enregistrée</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto' }}>
              {history.slice(0, 10).map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', borderRadius: '8px', padding: '8px 12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.activity_name || h.activity}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{h.activity_date || (h.recorded_at ? new Date(h.recorded_at).toLocaleDateString('fr-FR') : '')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Boutons action ── */}
        <div style={{ padding: '16px 20px 32px', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
          {alreadyPresent ? (
            <div style={{ width: '100%', padding: '15px', background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '12px', textAlign: 'center', color: '#16a34a', fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Déjà marqué présent pour cette activité
            </div>
          ) : (
            <button
              onClick={() => onPresent(participant)}
              style={{ width: '100%', padding: '16px', background: '#1e40af', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 4px 12px rgba(30,64,175,0.3)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Marquer Présent
            </button>
          )}
          <button onClick={onClose} style={{ width: '100%', padding: '13px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Modal sélection église + login (si non connecté) ─── */
function AccueilLoginGate({ onSuccess }) {
  const [step, setStep] = useState('church');
  const [churches, setChurches] = useState([]);
  const [loadingChurches, setLoadingChurches] = useState(true);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${_API}/churches/public/`)
      .then(r => setChurches(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoadingChurches(false));
  }, []);

  const [loginErrors, setLoginErrors] = useState({});
  const handleLogin = async (e) => {
    e.preventDefault();
    const found = checkRequired({ username, password }, { username: 'Identifiant', password: 'Mot de passe' });
    setLoginErrors(found);
    if (Object.keys(found).length) return;
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${_API}/token/`, { username: username.trim(), password });
      localStorage.setItem('access_token', res.data.access);
      localStorage.setItem('refresh_token', res.data.refresh);
      onSuccess();
    } catch {
      setError('Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const inp = { width: '100%', padding: '13px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', color: '#0f172a', background: '#f8fafc' };
  const accent = '#7c3aed';

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#ede9fe,#dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'system-ui,sans-serif' }}>
      <div style={{ background: 'white', borderRadius: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 32px 80px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', padding: '24px 28px 20px', position: 'relative' }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {selectedChurch?.logo_url
              ? <img src={selectedChurch.logo_url} alt="" style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)' }} />
              : <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="5"/><line x1="10" y1="3" x2="14" y2="3"/><path d="M4 9l8-4 8 4v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z"/><rect x="9" y="14" width="6" height="6" rx="0.5"/></svg>
                </div>
            }
            <div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Accueil Entrée</div>
              <div style={{ color: 'white', fontWeight: 800, fontSize: '1.05rem' }}>
                {step === 'church' ? 'Choisissez votre église' : selectedChurch?.name || 'Connexion'}
              </div>
              {step === 'login' && selectedChurch?.pastor && (
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>✝ Pasteur {selectedChurch.pastor}</div>
              )}
            </div>
          </div>
          {/* Étapes */}
          <div style={{ display: 'flex', gap: 6, marginTop: 16 }}>
            {['Église', 'Connexion'].map((lbl, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: (step === 'login' ? i <= 1 : i === 0) ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, color: accent }}>
                  {i === 0 && step === 'login' ? '✓' : i + 1}
                </div>
                <span style={{ color: (step === 'login' ? true : i === 0) ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', fontSize: '0.72rem', fontWeight: 600 }}>{lbl}</span>
                {i === 0 && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '24px 28px 28px' }}>
          {step === 'church' ? (
            <>
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 14px' }}>Sélectionnez l'église pour cet accueil</p>
              {loadingChurches ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>Chargement...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '300px', overflowY: 'auto' }}>
                  {churches.map(ch => (
                    <button key={ch.id} onClick={() => { setSelectedChurch(ch); setStep('login'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: 'white', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = '#faf5ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}>
                      {ch.logo_url
                        ? <img src={ch.logo_url} alt="" style={{ width: 38, height: 38, borderRadius: 9, objectFit: 'cover', flexShrink: 0 }} />
                        : <div style={{ width: 38, height: 38, borderRadius: 9, flexShrink: 0, background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1rem' }}>{ch.name[0]}</div>
                      }
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{ch.name}</div>
                        {ch.pastor && <div style={{ fontSize: '0.73rem', color: accent, fontWeight: 600 }}>✝ Pasteur {ch.pastor}</div>}
                        {ch.city && <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>📍 {ch.city}</div>}
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <button onClick={() => { setStep('church'); setError(''); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.8rem', cursor: 'pointer', marginBottom: 16, padding: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                Changer d'église
              </button>
              <form onSubmit={handleLogin} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Identifiant<Req /></label>
                  <input type="text" autoFocus value={username} onChange={e => setUsername(e.target.value)} placeholder="Nom d'utilisateur" style={{ ...inp, ...(loginErrors.username ? { borderColor: ERROR_COLOR } : {}) }}
                    onFocus={e => { e.target.style.borderColor = accent; e.target.style.background = 'white'; }}
                    onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }} />
                  <FieldError message={loginErrors.username} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Mot de passe<Req /></label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={{ ...inp, ...(loginErrors.password ? { borderColor: ERROR_COLOR } : {}) }}
                    onFocus={e => { e.target.style.borderColor = accent; e.target.style.background = 'white'; }}
                    onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }} />
                  <FieldError message={loginErrors.password} />
                </div>
                {error && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 14px', color: '#dc2626', fontSize: '0.83rem' }}>{error}</div>
                )}
                <button type="submit" disabled={loading} style={{ width: '100%', padding: '13px', background: loading ? '#a78bfa' : 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                  {loading ? 'Connexion...' : "Accéder à l'accueil"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MobileAccueil() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('access_token'));
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [count, setCount] = useState(0);
  const [showQR, setShowQR] = useState(false);
  const [scannedParticipant, setScannedParticipant] = useState(null);
  const [searchCode, setSearchCode] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [liveSuggestions, setLiveSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentPresents, setRecentPresents] = useState([]);
  const [alreadyPresentInfo, setAlreadyPresentInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('accueil'); // 'accueil' | 'liste'
  const [attendanceList, setAttendanceList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const selectedActivityRef = useRef(selectedActivity);
  useEffect(() => { selectedActivityRef.current = selectedActivity; }, [selectedActivity]);

  // Charger la liste complète quand on change d'activité ou d'onglet
  const fetchList = useCallback(async (actId) => {
    if (!actId) return;
    setLoadingList(true);
    try {
      const res = await api.get(`/attendance/records/?activity=${actId}&page_size=200&ordering=recorded_at`);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setAttendanceList(data);
    } catch {} finally { setLoadingList(false); }
  }, []);

  useEffect(() => {
    if (selectedActivity) fetchList(selectedActivity);
  }, [selectedActivity, fetchList]);

  const printReport = () => {
    const act = activities.find(a => String(a.id) === selectedActivity);
    const date = act?.date || new Date().toLocaleDateString('fr-FR');
    const rows = attendanceList.map((r, i) => {
      const p = r.participant_detail || {};
      const heure = r.recorded_at ? new Date(r.recorded_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
      const nom = p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';
      const statut = STATUS_LABELS[p.status] || p.status || '—';
      const code = p.member_code || '—';
      const genre = p.gender ? (p.gender === 'homme' ? 'H' : p.gender === 'femme' ? 'F' : 'E') : '—';
      return `<tr><td>${i + 1}</td><td>${nom}</td><td>${code}</td><td>${statut}</td><td>${genre}</td><td>${heure}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"/>
      <title>Rapport de présence — ${act?.name || ''}</title>
      <style>
        body { font-family: Arial, sans-serif; color: #1e293b; padding: 24px; }
        h1 { font-size: 1.3rem; margin-bottom: 4px; }
        .meta { font-size: 0.85rem; color: #64748b; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        th { background: #1e40af; color: white; padding: 8px 10px; text-align: left; }
        td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
        tr:nth-child(even) td { background: #f8fafc; }
        .total { margin-top: 16px; font-weight: bold; font-size: 0.95rem; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h1>Rapport de présence</h1>
      <div class="meta">
        Activité : <strong>${act?.name || '—'}</strong> &nbsp;|&nbsp;
        Date : <strong>${date}</strong> &nbsp;|&nbsp;
        Heure : <strong>${act?.start_time?.slice(0,5) || '—'}</strong> &nbsp;|&nbsp;
        Lieu : <strong>${act?.location || '—'}</strong>
      </div>
      <table><thead><tr><th>#</th><th>Nom</th><th>Code</th><th>Statut</th><th>Genre</th><th>Heure arrivée</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#94a3b8">Aucune présence</td></tr>'}</tbody></table>
      <div class="total">Total : ${attendanceList.length} présence${attendanceList.length > 1 ? 's' : ''}</div>
      <script>window.onload=()=>{window.print();}</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  // ── WebSocket temps réel ──
  const wsRef = useRef(null);
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const url = token ? `${WS_URL}?token=${token}` : WS_URL;
    const connect = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.event === 'attendance_update') {
            // Mettre à jour le compteur si c'est l'activité sélectionnée
            if (String(msg.activity_id) === String(selectedActivityRef.current)) {
              setCount(msg.count);
              fetchList(selectedActivityRef.current);
              // Ajouter dans les récents si pas déjà là
              if (msg.participant_id) {
                setRecentPresents(prev => {
                  const exists = prev.some(p => p.id === msg.participant_id);
                  if (exists) return prev;
                  return [{
                    id: msg.participant_id,
                    full_name: msg.participant_name,
                    first_name: msg.participant_name?.split(' ')[0] || '',
                    last_name: msg.participant_name?.split(' ').slice(1).join(' ') || '',
                    photo_url: msg.participant_photo,
                    status: msg.participant_status,
                    member_code: msg.participant_code,
                    _by: msg.recorded_by,
                  }, ...prev].slice(0, 8);
                });
              }
            }
          }
        } catch {}
      };
      ws.onclose = () => { setTimeout(connect, 5000); };
    };
    connect();
    return () => { wsRef.current?.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get(`/activities/?date_from=${today}&ordering=date,start_time&page_size=10`)
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : r.data.results || [];
        setActivities(data);
        if (data.length > 0) setSelectedActivity(String(data[0].id));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedActivity) return;
    api.get(`/attendance/records/?activity=${selectedActivity}&page_size=1`)
      .then((r) => {
        const total = r.data.count || (Array.isArray(r.data) ? r.data.length : 0);
        setCount(total);
      })
      .catch(() => {});
  }, [selectedActivity]);

  const handleQRScan = useCallback(async (qrCode) => {
    setShowQR(false);
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    try {
      let data;
      if (uuidRegex.test(qrCode)) {
        const res = await api.get(`/participants/fidele/profile/${qrCode}/`);
        data = res.data;
      } else {
        const res = await api.post('/participants/fidele/login/', { code: qrCode.trim().toUpperCase() });
        data = res.data;
      }
      setScannedParticipant(data);
    } catch {
      toast.error('QR code invalide ou participant introuvable.');
    }
  }, []);

  const handleCodeSearch = async (e) => {
    e.preventDefault();
    if (!searchCode.trim()) return;
    setSearchLoading(true);
    try {
      const res = await api.get(`/participants/?search=${encodeURIComponent(searchCode.trim())}&page_size=20`);
      const results = Array.isArray(res.data) ? res.data : res.data.results || [];
      if (results.length === 0) {
        toast.error('Aucun participant trouvé.');
      } else if (results.length === 1) {
        setScannedParticipant(results[0]);
        setSearchCode('');
      } else {
        setSearchResults(results);
        setSearchCode('');
      }
    } catch {
      toast.error('Erreur de recherche.');
    } finally {
      setSearchLoading(false); }
  };

  useEffect(() => {
    const q = searchCode.trim();
    if (q.length < 2) { setLiveSuggestions([]); setShowSuggestions(false); return; }
    const timer = setTimeout(() => {
      api.get(`/participants/?search=${encodeURIComponent(q)}&page_size=8`)
        .then(res => {
          const results = Array.isArray(res.data) ? res.data : res.data.results || [];
          setLiveSuggestions(results);
          setShowSuggestions(true);
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [searchCode]);

  const selectSuggestion = (p) => {
    setScannedParticipant(p);
    setSearchCode('');
    setLiveSuggestions([]);
    setShowSuggestions(false);
  };

  const markPresent = async (participant) => {
    setScannedParticipant(null);
    if (!selectedActivity) { toast.error('Sélectionnez une activité.'); return; }
    try {
      await api.post('/attendance/records/', {
        activity: parseInt(selectedActivity),
        participant: participant.id,
        is_anonymous: false,
      });
      // Le compteur sera mis à jour via WebSocket — on l'incrémente aussi localement
      setCount((n) => n + 1);
      setRecentPresents((prev) => {
        const exists = prev.some(p => p.id === participant.id);
        if (exists) return prev;
        return [participant, ...prev].slice(0, 8);
      });
      toast.success(`${participant.first_name || participant.full_name} — Présent(e) !`);
    } catch (err) {
      if (err.response?.status === 400) {
        setAlreadyPresentInfo(participant);
      } else {
        toast.error("Erreur d'enregistrement.");
      }
    }
  };

  // Si non authentifié → afficher le gate église + login
  if (!isAuthenticated) {
    return <AccueilLoginGate onSuccess={() => setIsAuthenticated(true)} />;
  }

  const currentActivity = activities.find((a) => String(a.id) === selectedActivity);
  const now = new Date();
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', fontFamily: 'system-ui, sans-serif', maxWidth: '480px', margin: '0 auto' }}>
      <Toaster position="top-center" toastOptions={{ style: { borderRadius: '12px', fontSize: '0.9rem' } }} />

      {/* ══ HEADER CLAIR ══ */}
      <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '0', position: 'sticky', top: 0, zIndex: 20, boxShadow: '0 2px 12px rgba(30,64,175,0.08)' }}>
        {/* Barre colorée en haut */}
        <div style={{ height: '4px', background: 'linear-gradient(90deg, #1e40af 0%, #7c3aed 50%, #1e40af 100%)', backgroundSize: '200% 100%' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '11px', background: 'linear-gradient(135deg, #1e40af, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 8px rgba(30,64,175,0.3)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </div>
            <div>
              <div style={{ color: '#0f172a', fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.1 }}>Accueil Entrée</div>
              <div style={{ color: '#94a3b8', fontSize: '0.66rem', textTransform: 'capitalize', marginTop: '1px' }}>{dateStr}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e40af', fontVariantNumeric: 'tabular-nums', marginRight: '4px' }}>{timeStr}</div>
            <a href="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '9px', padding: '7px 12px', color: '#1e40af', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 700 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              Admin
            </a>
          </div>
        </div>

        {/* Sélecteur activité */}
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <select value={selectedActivity} onChange={e => setSelectedActivity(e.target.value)}
              style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: '10px', border: '2px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', fontSize: '0.88rem', fontWeight: 600, appearance: 'none', cursor: 'pointer', outline: 'none' }}>
              {activities.length === 0 && <option value="">Aucune activité aujourd'hui</option>}
              {activities.map(a => (
                <option key={a.id} value={a.id}>{a.start_time?.slice(0, 5)} · {a.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ══ COMPTEUR ══ */}
      <div style={{ padding: '16px 16px 0' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 50%, #7c3aed 100%)', borderRadius: '20px', padding: '20px', display: 'flex', gap: '12px', boxShadow: '0 8px 24px rgba(30,64,175,0.25)', overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>Présences enregistrées</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ fontSize: '3.5rem', fontWeight: 900, color: 'white', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
            </div>
            {currentActivity && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', marginTop: '4px', fontStyle: 'italic' }}>{currentActivity.name}</div>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase' }}>Session</div>
              <div style={{ color: 'white', fontWeight: 900, fontSize: '1.4rem', lineHeight: 1 }}>{recentPresents.length}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(74,222,128,0.2)', borderRadius: '8px', padding: '5px 10px' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#4ade80', flexShrink: 0, animation: 'pulse 2s infinite' }} />
              <span style={{ color: '#4ade80', fontWeight: 700, fontSize: '0.7rem' }}>En direct</span>
            </div>
          </div>
        </div>
      </div>

      {/* ══ ONGLETS ══ */}
      <div style={{ display: 'flex', background: 'white', margin: '14px 16px 0', borderRadius: '14px', padding: '4px', gap: '4px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
        {[
          { key: 'accueil', label: 'Accueil', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="16" y="3" width="5" height="5" rx="1"/><rect x="3" y="16" width="5" height="5" rx="1"/><path d="M21 16h-3v3"/><path d="M18 21h3"/><path d="M16 16v-1"/></svg> },
          { key: 'liste', label: `Liste · ${count}`, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg> },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ flex: 1, padding: '10px 8px', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.15s',
              background: activeTab === tab.key ? 'linear-gradient(135deg, #1e40af, #2563eb)' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#64748b',
              boxShadow: activeTab === tab.key ? '0 3px 8px rgba(30,64,175,0.3)' : 'none',
            }}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* ══ ONGLET ACCUEIL ══ */}
      {activeTab === 'accueil' && (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Bouton scanner — hero */}
          <button onClick={() => setShowQR(true)}
            style={{ width: '100%', padding: '18px 20px', background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)', color: 'white', border: 'none', borderRadius: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 6px 20px rgba(30,64,175,0.35)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '-15px', right: '-15px', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
            <div style={{ width: '48px', height: '48px', borderRadius: '13px', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="16" y="3" width="5" height="5" rx="1"/><rect x="3" y="16" width="5" height="5" rx="1"/><path d="M21 16h-3v3"/><path d="M18 21h3"/><path d="M16 16v-1"/></svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: '-0.01em' }}>Scanner une carte QR</div>
              <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.65)', marginTop: '2px' }}>Pointer via la caméra du téléphone</div>
            </div>
          </button>

          {/* Recherche */}
          <form onSubmit={handleCodeSearch} style={{ display: 'flex', gap: '8px', position: 'relative' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="Nom, prénom ou code…" value={searchCode}
                onChange={e => setSearchCode(e.target.value)}
                onFocus={() => { if (liveSuggestions.length > 0) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                style={{ width: '100%', padding: '12px 12px 12px 36px', borderRadius: '12px', border: '2px solid #e2e8f0', fontSize: '0.9rem', background: 'white', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }} />
              {showSuggestions && liveSuggestions.length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', zIndex: 50, maxHeight: '280px', overflowY: 'auto' }}>
                  {liveSuggestions.map(p => (
                    <button key={p.id} type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectSuggestion(p)}
                      style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'white', cursor: 'pointer' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, color: '#1e40af', flexShrink: 0 }}>
                        {(p.first_name?.[0] || '') + (p.last_name?.[0] || '')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.86rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.full_name || `${p.first_name} ${p.last_name}`}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.member_code}{p.phone ? ` · ${p.phone}` : ''}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" disabled={searchLoading}
              style={{ padding: '12px 18px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem', flexShrink: 0 }}>
              {searchLoading ? '…' : 'OK'}
            </button>
          </form>

          {/* Aucune activité */}
          {!currentActivity && activities.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ display: 'block', margin: '0 auto 10px' }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Aucune activité programmée aujourd'hui</div>
            </div>
          )}

          {/* Dernières présences session */}
          {recentPresents.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px', padding: '0 2px' }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite', flexShrink: 0 }} />
                <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#94a3b8' }}>Derniers pointages</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {recentPresents.map((p, i) => {
                  const sc = STATUS_COLORS[p.status] || STATUS_COLORS.invite;
                  const initials = `${p.first_name?.[0] || p.full_name?.[0] || '?'}${p.last_name?.[0] || ''}`.toUpperCase();
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '11px', background: 'white', borderRadius: '14px', padding: '10px 14px', border: '1px solid #f1f5f9', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                      {p.photo_url
                        ? <img src={p.photo_url} alt="" style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${sc.dot}30` }} />
                        : <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: `${sc.dot}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `2px solid ${sc.dot}25` }}>
                            <span style={{ fontSize: '0.9rem', fontWeight: 900, color: sc.dot }}>{initials}</span>
                          </div>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.first_name || p.full_name} {p.last_name || ''}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <span style={{ color: sc.dot, fontWeight: 600 }}>{STATUS_LABELS[p.status] || p.status}</span>
                          {p.member_code && <span>· {p.member_code}</span>}
                          {p._by && <span>· {p._by}</span>}
                        </div>
                      </div>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ ONGLET LISTE ══ */}
      {activeTab === 'liste' && (
        <div style={{ padding: '14px 16px 32px' }}>
          <button onClick={printReport}
            style={{ width: '100%', padding: '13px', background: 'white', color: '#1e40af', border: '2px solid #bfdbfe', borderRadius: '13px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px', boxShadow: '0 1px 4px rgba(30,64,175,0.08)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Imprimer le rapport · {attendanceList.length} présence{attendanceList.length > 1 ? 's' : ''}
          </button>

          {loadingList ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Chargement...</div>
          ) : attendanceList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', fontSize: '0.88rem' }}>Aucune présence enregistrée</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {attendanceList.map((r, i) => {
                const p = r.participant_detail || {};
                const sc = STATUS_COLORS[p.status] || STATUS_COLORS.invite;
                const initials = `${p.first_name?.[0] || '?'}${p.last_name?.[0] || ''}`.toUpperCase();
                const heure = r.recorded_at
                  ? new Date(r.recorded_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '—';
                return (
                  <div key={r.id} style={{ background: 'white', borderRadius: '14px', padding: '11px 13px', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div style={{ width: '22px', flexShrink: 0, fontSize: '0.68rem', color: '#cbd5e1', fontWeight: 700, textAlign: 'center' }}>{i + 1}</div>
                    {p.photo_url
                      ? <img src={p.photo_url} alt="" style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${sc.dot}25` }} />
                      : <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: `${sc.dot}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `2px solid ${sc.dot}20` }}>
                          <span style={{ fontSize: '0.88rem', fontWeight: 900, color: sc.dot }}>{initials}</span>
                        </div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—'}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '2px' }}>
                        <span style={{ color: sc.dot, fontWeight: 600 }}>{STATUS_LABELS[p.status] || '—'}</span>
                        {p.member_code && <span>· {p.member_code}</span>}
                        {r.recorded_by_name && <span>· {r.recorded_by_name}</span>}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right', background: '#eff6ff', borderRadius: '8px', padding: '5px 9px' }}>
                      <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.88rem', color: '#1e40af' }}>{heure}</div>
                      <div style={{ fontSize: '0.58rem', color: '#93c5fd', marginTop: '1px' }}>arrivée</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {showQR && <QRScanner onScan={handleQRScan} onClose={() => setShowQR(false)} />}
      {searchResults && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget) setSearchResults(null); }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '20px', maxWidth: '480px', width: '100%', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>{searchResults.length} résultats — choisissez</div>
              <button onClick={() => setSearchResults(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {searchResults.map(p => (
              <button key={p.id}
                onClick={() => { setScannedParticipant(p); setSearchResults(null); }}
                style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', marginBottom: '8px', cursor: 'pointer' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 800, color: '#1e40af', flexShrink: 0 }}>
                  {(p.first_name?.[0] || '') + (p.last_name?.[0] || '')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a' }}>{p.full_name || `${p.first_name} ${p.last_name}`}</div>
                  <div style={{ fontSize: '0.76rem', color: '#64748b' }}>{p.member_code}{p.phone ? ` · ${p.phone}` : ''}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {scannedParticipant && (
        <ParticipantCard
          participant={scannedParticipant}
          activityId={selectedActivity}
          onPresent={markPresent}
          onClose={() => setScannedParticipant(null)}
        />
      )}

      {/* ── Modal déjà présent ── */}
      {alreadyPresentInfo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: 'white', borderRadius: '24px', padding: '28px 24px', maxWidth: '340px', width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#ca8a04" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Déjà enregistré</div>
            <div style={{ fontSize: '0.88rem', color: '#64748b', marginBottom: '4px' }}>
              <strong style={{ color: '#0f172a' }}>{alreadyPresentInfo.first_name || alreadyPresentInfo.full_name} {alreadyPresentInfo.last_name || ''}</strong>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '22px' }}>est déjà marqué(e) présent(e) pour cette activité.</div>
            <button onClick={() => setAlreadyPresentInfo(null)}
              style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #1e40af, #2563eb)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(30,64,175,0.3)' }}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
